/**
 * Staff claim parsing. The dashboard mints only the BOOLEAN claim shape (super_admin /
 * center_admin + camelCase centerId); the legacy string shape (role:'superAdmin'|'centerAdmin'
 * + snake_case center_id) is read-side legacy only (docs/05). Authorization is always the ID
 * token claim, never the users/{uid} mirror (docs/05 session handling).
 */
import type { ParsedToken } from 'firebase/auth';
import { portalPaths } from '@/app/routes/portal-paths';
import type { AppSurface } from '@/lib/backend/config';

/** Normalised clinician level. The therapist custom token carries `role:'l{N}Therapist'`. */
export type TherapistLevel = 'L1' | 'L2' | 'L3';

export interface StaffClaims {
  superAdmin: boolean;
  centerAdmin: boolean;
  /** camelCase claim key (docs/05). */
  centerId: string | null;
  /** The branch_admin claim (spec 2026-09-06 §3.1). Never coexists with centerAdmin. */
  branchAdmin: boolean;
  /** The branch a branchAdmin grant is pinned to; null for every other grant. */
  branchId: string | null;
  canQueryConsentHistory: boolean;
  /** Mobile-side clinical role claim ('l3Therapist' etc.) - present on DUAL-ROLE
   *  accounts (independent L3 acting as a center, BRD §2.4.1). Display-scope
   *  only: it unlocks convenience widgets (e.g. the SCH quarter card), never a
   *  route - authorization stays claims-gated on the staff flags above. */
  therapistRole?: string | null;
  /**
   * The custom-token lane (`app_kind`). 'therapist' for any therapist account — INCLUDING one still
   * Pending (registered, not yet credentialed), which carries `app_kind:'therapist'` but no level.
   * That's how a Pending therapist is told apart from a genuinely no-access session.
   */
  appKind: string | null;
  /**
   * A therapist session (custom token from the mobile lane, `app_kind:'therapist'`). Non-null only
   * for clinicians; the admin boolean claims above stay false for them. Drives the portal.
   */
  therapistLevel: TherapistLevel | null;
}

function normalizeTherapistLevel(role: unknown): TherapistLevel | null {
  switch (role) {
    case 'L1':
    case 'l1Therapist':
      return 'L1';
    case 'L2':
    case 'l2Therapist':
      return 'L2';
    case 'L3':
    case 'l3Therapist':
      return 'L3';
    default:
      return null;
  }
}

export function parseClaims(raw: ParsedToken): StaffClaims {
  const superAdmin = raw.super_admin === true;
  const centerAdmin = raw.center_admin === true;
  const centerId = typeof raw.centerId === 'string' ? raw.centerId : null;
  const branchAdmin = raw.branch_admin === true;
  const branchId = typeof raw.branchId === 'string' && raw.branchId !== '' ? raw.branchId : null;
  const canQueryConsentHistory = raw.canQueryConsentHistory === true;
  // Dual-role display claim (SCH card etc.) — the raw role string, if any.
  const therapistRole = typeof raw.role === 'string' && raw.role !== '' ? raw.role : null;
  // Therapist tokens carry app_kind:'therapist' + role:'l{N}Therapist' (mobile custom token).
  const appKind = typeof raw.app_kind === 'string' ? raw.app_kind : null;
  const therapistLevel = appKind === 'therapist' ? normalizeTherapistLevel(raw.role) : null;
  return {
    superAdmin,
    centerAdmin,
    centerId,
    branchAdmin,
    branchId,
    canQueryConsentHistory,
    therapistRole,
    appKind,
    therapistLevel,
  };
}

/**
 * Whether these claims grant ANY dashboard shell (docs/05 role -> capability matrix). Note the
 * consent-auditor claim (canQueryConsentHistory) is a first-class persona: the backend authorizes
 * queryUserConsentHistory on that claim ALONE (no super_admin), so a pure auditor must be admitted
 * to a LIMITED shell - its routes are then gated to Compliance + Settings only (see router.tsx).
 */
export function hasStaffAccess(c: StaffClaims): boolean {
  return c.superAdmin || c.centerAdmin || c.branchAdmin || c.canQueryConsentHistory;
}

/** A clinician session — routed to the portal (center.aabar.io), never the admin site. */
export const isTherapist = (c: StaffClaims): boolean => c.therapistLevel !== null;

/**
 * May enter the portal. A clinician works there; a Center Admin now runs
 * their centre there too (spec 2026-09-02 §6). A pure Super Admin does not —
 * the admin panel is their surface.
 */
export const canEnterT = (c: StaffClaims): boolean =>
  c.therapistLevel !== null || c.centerAdmin || c.branchAdmin;

/**
 * May enter the admin panel (admin.aabar.io, spec 2026-09-21 host split). Same rule as the old
 * `isStaff` compliance carve-out (super admin OR the bespoke consent-auditor claim) — a Center
 * Admin / Branch Admin / therapist has no business here, even a dual-claim one still gets `siteFor`
 * below to report 'both'.
 */
export const canEnterAdmin = (c: StaffClaims): boolean => c.superAdmin || c.canQueryConsentHistory;

/**
 * Which site(s) this account belongs on (spec 2026-09-21 host split). A dual-claim account (e.g. a
 * Super Admin who is also a Center Admin) legitimately belongs on both — `RequireAuth` /
 * `RequireTherapist` each check only their own half of this, so 'both' never itself gates anything.
 */
export function siteFor(c: StaffClaims): 'admin' | 'portal' | 'both' {
  const a = canEnterAdmin(c);
  const p = canEnterT(c);
  return a && p ? 'both' : a ? 'admin' : 'portal';
}

/** May see the Centre section inside the portal. */
export const isCentreMgr = (c: StaffClaims): boolean => c.centerAdmin;

/** May see the Branch section inside the portal (spec 2026-09-06 §9.1). */
export const isBranchMgr = (c: StaffClaims): boolean => c.branchAdmin;
/** Manages SOME scope inside the portal — the seven scope screens open for either grant. */
export const isAnyMgr = (c: StaffClaims): boolean => c.centerAdmin || c.branchAdmin;

/**
 * May create a calendar event (spec 2026-09-28 D9): an L3 therapist, a centre admin or a branch
 * admin. L1 and L2 lost creation in the rework; a Super Admin lost it with owner decision 12
 * (2026-10-18: audited read-only clinical access, no event create/cancel).
 *
 * Mirrors the server's `callerMayCreate` (aabar-backend functions/src/events/validation.ts),
 * which reads the admin GRANTS as well as the role string — so a dual-role account (an L2 who is
 * also a Centre Admin) is admitted here for the same reason it is admitted there.
 */
export const canCreateEvent = (c: StaffClaims): boolean =>
  c.centerAdmin || c.branchAdmin || c.therapistLevel === 'L3';

/**
 * A therapist account that has registered but is not yet credentialed (no level). The backend still
 * grants it `app_kind:'therapist'`, so it can self-complete the profile + submit for review, but it
 * has NO dashboard access until an admin verifies it — so it belongs in onboarding, not the portal.
 */
// A Center Admin now signs in on the therapist lane too, so verify.ts mints them the same
// app_kind:'therapist' + no-level shape as a genuinely pending therapist — exclude them here.
export const isPendingTherapist = (c: StaffClaims): boolean =>
  c.appKind === 'therapist' && c.therapistLevel === null && !c.centerAdmin && !c.branchAdmin;

/** Anyone the app admits to SOME workspace (admin staff OR a therapist). */
export function hasAnyAccess(c: StaffClaims): boolean {
  return hasStaffAccess(c) || isTherapist(c);
}

/**
 * The single admission rule for a freshly-read token. Both the sign-in listener
 * (auth-provider.tsx) and the forced refresh (refresh.ts) MUST use this — they
 * previously disagreed (hasAnyAccess vs hasStaffAccess), so a pure therapist who
 * refreshed their token was demoted to noAccess and bounced to the therapist sign-in.
 */
export function sessionStatusFor(c: StaffClaims): 'signedIn' | 'noAccess' {
  return hasAnyAccess(c) ? 'signedIn' : 'noAccess';
}

// Claim predicates for route gating (docs/05 role -> capability matrix).
export const isSuperAdmin = (c: StaffClaims) => c.superAdmin;
/** The admin panel is a Super Admin's surface. A Center Admin runs their centre from the portal. */
export const isStaff = (c: StaffClaims) => c.superAdmin;
export const isConsentAuditor = (c: StaffClaims) => c.canQueryConsentHistory;
/** Compliance is reachable by super admins and the bespoke consent-auditor claim. */
export const canSeeCompliance = (c: StaffClaims) => c.superAdmin || c.canQueryConsentHistory;

/**
 * Where a freshly-signed-in session should land, LOCAL to the surface it landed on (spec
 * 2026-09-21 host split). The portal is one workspace, so any admitted portal account lands on its
 * home; the admin site still has the Compliance carve-out for a pure consent auditor. `RequireAuth`
 * / `RequireTherapist` are what keep an account that doesn't belong on a surface off it in the
 * first place (see WrongSiteNotice) — this function no longer cross-references the other site.
 */
export function landingPath(c: StaffClaims | null, surface: AppSurface): string {
  if (surface === 'portal') return portalPaths.home;
  if (c && !isStaff(c) && c.canQueryConsentHistory) return '/compliance';
  return '/overview';
}
